import { Grid, Theme, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { get as _get } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { emailAdjuster } from '../../../api/emailAdjusterApi';
import { useAppSelector } from '../../../app/hooks/hooks';
import { getAuthKey, getClaimNumber, getSessionId, getUrlKey, getUserData } from '../../../app/store/combinedSelectors';
import { cancelBtn } from '../../../Constants/Constants';
import { ContactAdjusterInternalEmail } from '../../../types/type';
import CallToAction from '../../shared/CallToAction/CallToAction';
import EmailInput from '../../shared/EmailInput/EmailInput';
import FreeformText from '../../shared/FreeformText/FreeformText';
import PhoneInput from '../../shared/PhoneInput/PhoneInput';
import AdjusterConfirmation from '../AdjusterConfirmation/AdjusterConfirmation';
import ModalLinearActivityIndicator from '../ModalLinearActivityIndicator/ModalLinearActivityIndicator';

export default function AdjusterContact() {
  const navigate = useNavigate();
  const theme: Theme = useTheme();
  const [count, setCount] = useState(0);
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [phone, setPhone] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [modalLoadingOpen, setModalLoadingOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const claimNumber = useSelector(getClaimNumber);
  const customerData = useSelector(getUserData);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);

  useEffect(() => {
    setEmail(customerData.emailAddress ? customerData.emailAddress : '');
    setPhone(customerData.phoneNumber ? customerData.phoneNumber : '');
  }, [customerData.emailAddress, customerData.phoneNumber]);

  async function handleSubmit() {
    // Trigger API call to send message
    // Make web service call to create a service
    const response = await sendEmail();
    // Hide LoadingIndicator
    setModalLoadingOpen(false);
    if (response) {
      // Go to the next screen
      setSuccess(true);
    } else {
      setSuccess(false);
    }
    setSubmitted(true);
  }

  async function sendEmail() {
    // Show LoadingIndicator
    setModalLoadingOpen(true);
    const postBody: ContactAdjusterInternalEmail = {
      firstName: customerData.firstName,
      lastName: customerData.lastName,
      email: email,
      phone: phone,
      comment: comment,
      claimNumber: claimNumber,
      adjusterEmail: _get(claimSummary, 'adjusterInfo.adjusterEmail', ''),
      adjusterName: _get(claimSummary, 'adjusterInfo.adjusterName', ''),
    };
    const response: any = await emailAdjuster(urlKey, sessionId, authKey, postBody);
    return response;
  }

  async function goToClaimsOverview() {
    navigate(-1);
  }

  function charCount(event: string) {
    setComment(event);
    setCount(event.length);
  }

  function emailChange(event: string) {
    setEmail(event);
  }

  function emailValid(event: boolean) {
    setIsEmailValid(event);
  }

  function phoneChange(event: string) {
    setPhone(event);
  }

  function phoneValid(event: boolean) {
    setIsPhoneValid(event);
  }

  let content = <div></div>;
  content = (
    <Box sx={{ width: '100%' }}>
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
      {!submitted && (
        <Grid
          container
          sx={{
            width: '100%',
            maxWidth: '1280px',
            minWidth: '310px',
            margin: '0 auto',
            marginTop: '1em',
          }}
        >
          <Grid item xs={12}>
            <Typography sx={theme.custom.styles.titleType1}>Contact Claims</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ marginTop: '20px' }} variant="body1">
              If you have questions on your claim, ask in the space provided and we'll contact you.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ marginTop: '10px' }} variant="body1">
              For your protection, don't include any personal data such as Social Security numbers, credit card numbers,
              or details of financial accounts.
            </Typography>
          </Grid>
          <Grid sx={{ marginTop: '2rem', marginBottom: '5px' }} item xs={12}>
            <Typography sx={theme.custom.styles.formLabelStyle}>Message</Typography>
          </Grid>
          <Grid item xs={12}>
            <FreeformText textUpdate={charCount} />
          </Grid>

          <Grid item xs={12} sx={{ marginTop: '2rem' }}>
            <Typography sx={theme.custom.styles.titleType4}>Please provide your contact information.</Typography>
          </Grid>
          <Grid item container spacing={2} sx={{ marginTop: '20px' }}>
            <Grid item xs={12} md={6}>
              <EmailInput
                isRequired={true}
                hideSubCase={false}
                emailValid={emailValid}
                defaultValue={customerData.emailAddress}
                emailValueUpdate={emailChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PhoneInput
                isRequired={true}
                hideSubCase={false}
                phoneValid={phoneValid}
                defaultValue={customerData.phoneNumber}
                phoneValueUpdate={phoneChange}
              />
            </Grid>
          </Grid>
          <Grid item xs={2}></Grid>
          <Grid sx={{ marginTop: '50px' }} item xs={12}>
            <CallToAction
              nextButton={true}
              nextButtonDisabled={count == 0 || !isPhoneValid || !isEmailValid}
              nextButtonContent={'Send'}
              handleNext={handleSubmit}
              skipButton={false}
              cancelButton={true}
              backButton={false}
              cancelButtonContent={cancelBtn}
              handleCancel={goToClaimsOverview}
            ></CallToAction>
          </Grid>
        </Grid>
      )}
      {!!submitted && (
        <Grid
          container
          sx={{
            width: '100%',
            maxWidth: '1280px',
            minWidth: '310px',
            margin: '0 auto',
            marginTop: '1em',
          }}
        >
          <AdjusterConfirmation success={success} comment={comment}></AdjusterConfirmation>
        </Grid>
      )}
    </Box>
  );
  return <Box sx={{ width: '100%' }}>{content}</Box>;
}
